/**
 * MasterDataDeliveryPlaceFormDialog — Create/Edit `delivery_place`.
 * Compact Sales Contract style dialog. Single-column.
 *
 * `delivery_place_code` là PK text: required, disable khi edit.
 */
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdDeliveryPlaceMutation,
  useLazyCheckMdDeliveryPlaceCodeQuery,
  useLazySuggestMdDeliveryPlaceCodeQuery,
  useUpdateMdDeliveryPlaceMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataDeliveryPlace } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleActionsSx, dialogTitleSx, dialogTitleTextSx, numSx, roSx } from '../purchase/dialogStyle'

// Access Format='Standard' → #,##0.00. type=text để giữ dấu phẩy; format on blur.
const parseNumeric = (s: string): number | null => {
  const t = (s ?? '').replace(/,/g, '').trim()
  if (t === '') return null
  const n = Number(t)
  return Number.isNaN(n) ? null : n
}
const formatStandard = (v: string | number | null | undefined): string => {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''))
  if (Number.isNaN(n)) return String(v)
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataDeliveryPlace | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  delivery_place_code: string
  delivery_place: string
  logistic_costs_to_fob: string
  address: string
}

const EMPTY: FormState = {
  delivery_place_code: '',
  delivery_place: '',
  logistic_costs_to_fob: '',
  address: '',
}

export default function MasterDataDeliveryPlaceFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createDeliveryPlace, { isLoading: creating }] = useCreateMdDeliveryPlaceMutation()
  const [updateDeliveryPlace, { isLoading: updating }] = useUpdateMdDeliveryPlaceMutation()
  const [triggerSuggest, suggestState] = useLazySuggestMdDeliveryPlaceCodeQuery()
  const [triggerCheck] = useLazyCheckMdDeliveryPlaceCodeQuery()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)
  const [showErrors, setShowErrors] = useState(false)
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  const [precheckBusy, setPrecheckBusy] = useState(false)
  const precheckBusyRef = useRef(false)

  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    setRegenNotice(null)
    setPrecheckBusy(false)
    precheckBusyRef.current = false
    if (isEdit && initialValue) {
      setForm({
        delivery_place_code: initialValue.delivery_place_code ?? '',
        delivery_place: initialValue.delivery_place ?? '',
        logistic_costs_to_fob: formatStandard(initialValue.logistic_costs_to_fob),
        address: initialValue.address ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  // New: auto-suggest mã kế tiếp (PLACE<nn>) khi mở — chỉ set khi field còn trống.
  useEffect(() => {
    if (!open || isEdit) return
    let cancelled = false
    void triggerSuggest(undefined, false).unwrap().then((res) => {
      if (cancelled) return
      const code = (res.delivery_place_code || '').trim()
      if (!code) return
      setForm((prev) => (prev.delivery_place_code.trim() ? prev : { ...prev, delivery_place_code: code }))
    }).catch(() => {})
    return () => { cancelled = true }
  }, [open, isEdit, triggerSuggest])

  // Dismiss alert khi user sửa mã khác mã vừa suggest.
  useEffect(() => {
    if (regenNotice && form.delivery_place_code !== regenNotice.to) setRegenNotice(null)
  }, [form.delivery_place_code, regenNotice])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const codeEmpty = !form.delivery_place_code.trim()
  const canSave = !codeEmpty && !loading

  // Blur mã → check trùng; nếu trùng thì suggest mã mới + báo inline (race-safe).
  const handleCodeBlur = async () => {
    if (isEdit) return
    const code = form.delivery_place_code.trim()
    if (!code) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await triggerCheck(code, false).unwrap()
      if (!check.exists) return
      const next = await triggerSuggest(undefined, false).unwrap()
      const newCode = (next.delivery_place_code || '').trim()
      if (newCode && newCode !== code) {
        setForm((prev) => ({ ...prev, delivery_place_code: newCode }))
        setRegenNotice({ from: code, to: newCode })
      }
    } catch {
      /* BE raise lúc Save nếu thật sự trùng */
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const buildBody = () => ({
    delivery_place: form.delivery_place.trim() || null,
    logistic_costs_to_fob: parseNumeric(form.logistic_costs_to_fob),
    address: form.address.trim() || null,
  })

  const handleSave = async () => {
    if (precheckBusyRef.current) return
    if (codeEmpty) {
      setShowErrors(true)
      return
    }
    try {
      if (isEdit && initialValue) {
        await updateDeliveryPlace({ code: initialValue.delivery_place_code, body: buildBody() }).unwrap()
        snack.success('Delivery place updated')
      } else {
        await createDeliveryPlace({ delivery_place_code: form.delivery_place_code.trim(), ...buildBody() }).unwrap()
        snack.success('Delivery place created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      const msg = getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create')
      const lower = msg.toLowerCase()
      // Race lọt qua blur-check → BE raise duplicate → tự suggest mã mới + alert.
      if (!isEdit && (lower.includes('exist') || lower.includes('duplicate'))) {
        try {
          const next = await triggerSuggest(undefined, false).unwrap()
          const newCode = (next.delivery_place_code || '').trim()
          const oldCode = form.delivery_place_code.trim()
          if (newCode && newCode !== oldCode) {
            setForm((prev) => ({ ...prev, delivery_place_code: newCode }))
            setRegenNotice({ from: oldCode, to: newCode })
            return
          }
        } catch {
          /* fallthrough */
        }
      }
      snack.error(msg)
    }
  }

  const codeHelper = isEdit
    ? 'Delivery Place Code cannot be changed after creation.'
    : precheckBusy
      ? 'Verifying code…'
      : suggestState.isFetching
        ? 'Suggesting next code…'
        : 'Suggested code below; edit if needed.'

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit Delivery Place' : 'New Delivery Place'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.delivery_place_code}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {regenNotice ? (
          <Alert severity="warning" sx={{ mb: 1.25, py: 0.25 }}>
            Delivery Place Code <b>{regenNotice.from}</b> was just created — suggested new code <b>{regenNotice.to}</b>.
          </Alert>
        ) : null}
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label="Delivery Place Code"
              size="small"
              fullWidth
              required
              disabled={isEdit}
              error={showErrors && codeEmpty}
              value={form.delivery_place_code}
              onChange={(e) => set('delivery_place_code', e.target.value)}
              onBlur={() => void handleCodeBlur()}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: isEdit ? -1 : 1 }}
              helperText={codeHelper}
              sx={isEdit ? roSx : undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Delivery Place"
              size="small"
              fullWidth
              value={form.delivery_place}
              onChange={(e) => set('delivery_place', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Logistic Costs To Fob"
              size="small"
              fullWidth
              value={form.logistic_costs_to_fob}
              onChange={(e) => set('logistic_costs_to_fob', e.target.value.replace(/[^0-9.,-]/g, ''))}
              onBlur={() => set('logistic_costs_to_fob', formatStandard(form.logistic_costs_to_fob))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 3, inputMode: 'decimal', style: { textAlign: 'right' } }}
              sx={numSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Address"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={form.address}
              onChange={(e) => set('address', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 4 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
